import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Share2, Download, Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SHARE_TEXT = 'I just received a medal from Brioritize.com';
const APP_URL = window.location.origin;

/* ── Gold Medal SVG with ribbon ── */
const GoldMedalSVG = ({ type = 'work', size = 160 }) => {
  // Different center emblems per medal type
  const renderEmblem = () => {
    if (type === 'personal') {
      // Heart
      return (
        <path
          d="M60 48c-3.2-6-10-8-15.5-5.5C39 45 36 50 37 55.5c1 5 6 10 23 18.5 17-8.5 22-13.5 23-18.5 1-5.5-2-10.5-7.5-13S63.2 42 60 48z"
          fill="#c5a340" stroke="#a08030" strokeWidth="1"
        />
      );
    }
    if (type === 'altruism') {
      // Laurel wreath
      return (
        <g fill="none" stroke="#c5a340" strokeWidth="1.5" strokeLinecap="round">
          {/* Left branch */}
          <path d="M46 70c0 0-4-8-2-16s6-14 6-14" />
          <path d="M44 62c-3-1-6 1-5 4s4 4 6 2" />
          <path d="M42 54c-3-2-7 0-6 3s4 5 7 3" />
          <path d="M43 46c-3-2-6 0-5 3s4 4 6 2" />
          {/* Right branch */}
          <path d="M74 70c0 0 4-8 2-16s-6-14-6-14" />
          <path d="M76 62c3-1 6 1 5 4s-4 4-6 2" />
          <path d="M78 54c3-2 7 0 6 3s-4 5-7 3" />
          <path d="M77 46c3-2 6 0 5 3s-4 4-6 2" />
        </g>
      );
    }
    // Work: 5-pointed star (default)
    return (
      <path
        d="M60 40l5.5 11.2 12.3 1.8-8.9 8.7 2.1 12.3L60 68l-11 5.8 2.1-12.3-8.9-8.7 12.3-1.8z"
        fill="#c5a340" stroke="#a08030" strokeWidth="1"
      />
    );
  };

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Ribbon - V shape */}
      <polygon points="36,0 42,0 60,32 78,0 84,0 60,40" fill="#E53935" />
      <polygon points="40,0 44,0 60,30 76,0 80,0 60,37" fill="#FFFFFF" />
      <polygon points="43,0 47,0 60,28 73,0 77,0 60,34" fill="#1E88E5" />
      {/* Ribbon border */}
      <line x1="36" y1="0" x2="60" y2="40" stroke="#333" strokeWidth="0.5" opacity="0.3"/>
      <line x1="84" y1="0" x2="60" y2="40" stroke="#333" strokeWidth="0.3" opacity="0.3"/>

      {/* Clasp */}
      <rect x="55" y="35" width="10" height="6" rx="1.5" fill="#d4a84b" stroke="#b8922f" strokeWidth="0.5"/>

      {/* Medal outer ring - darker gold */}
      <circle cx="60" cy="72" r="34" fill="url(#medalOuterGrad)" stroke="#8a6d20" strokeWidth="1"/>
      {/* Medal raised rim */}
      <circle cx="60" cy="72" r="31" fill="none" stroke="#c5a340" strokeWidth="1.5"/>
      {/* Medal inner face - lighter gold */}
      <circle cx="60" cy="72" r="28" fill="url(#medalInnerGrad)" stroke="#b8922f" strokeWidth="0.5"/>

      {/* Center emblem */}
      {renderEmblem()}

      {/* Shine highlight */}
      <ellipse cx="48" cy="58" rx="10" ry="14" fill="white" opacity="0.12" transform="rotate(-20 48 58)"/>

      {/* Gradients */}
      <defs>
        <radialGradient id="medalOuterGrad" cx="45%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#f0d060"/>
          <stop offset="50%" stopColor="#d4a84b"/>
          <stop offset="100%" stopColor="#a08030"/>
        </radialGradient>
        <radialGradient id="medalInnerGrad" cx="40%" cy="30%" r="65%">
          <stop offset="0%" stopColor="#fce588"/>
          <stop offset="40%" stopColor="#e8c84a"/>
          <stop offset="100%" stopColor="#c5a340"/>
        </radialGradient>
      </defs>
    </svg>
  );
};

/* ── Small medal for inline display ── */
export const GoldMedalIcon = ({ type = 'work', size = 48 }) => (
  <GoldMedalSVG type={type} size={size} />
);

/* ── Golden 5-pointed star particle ── */
const GoldenStar = ({ delay, x, y, size, rotation, duration }) => (
  <motion.div
    className="absolute pointer-events-none"
    initial={{ opacity: 0, scale: 0, x: 0, y: 0, rotate: 0 }}
    animate={{
      opacity: [0, 1, 1, 0.6, 0],
      scale: [0, 1.3, 1, 0.7, 0],
      x: x,
      y: y,
      rotate: rotation,
    }}
    transition={{ duration, delay, ease: 'easeOut' }}
    style={{ left: '50%', top: '50%' }}
  >
    <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`starGrad-${delay}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffe066"/>
          <stop offset="50%" stopColor="#ffd700"/>
          <stop offset="100%" stopColor="#daa520"/>
        </linearGradient>
      </defs>
      <path
        d="M16 2l4.1 8.3 9.2 1.3-6.6 6.5 1.6 9.1L16 22.5l-8.3 4.4 1.6-9.1L2.7 11.6l9.2-1.3z"
        fill={`url(#starGrad-${delay})`}
        stroke="#b8860b"
        strokeWidth="0.5"
      />
      {/* Inner highlight */}
      <path
        d="M16 6l2.8 5.7 6.3.9-4.5 4.4 1.1 6.2L16 20.2l-5.7 3 1.1-6.2-4.5-4.4 6.3-.9z"
        fill="none" stroke="#fff4c2" strokeWidth="0.3" opacity="0.5"
      />
    </svg>
  </motion.div>
);

const generateStars = (count = 20) => {
  const stars = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const radius = 100 + Math.random() * 180;
    stars.push({
      id: i,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      size: 18 + Math.random() * 20,
      delay: Math.random() * 0.5,
      duration: 1.4 + Math.random() * 0.6,
      rotation: Math.random() * 360,
    });
  }
  return stars;
};

const medalLabels = {
  work: 'Work Medal',
  personal: 'Personal Medal',
  altruism: 'Altruism Medal',
};

export const MedalCelebration = ({ medal, onClose }) => {
  const [stars, setStars] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [generating, setGenerating] = useState(false);
  const label = medalLabels[medal?.medal_type] || 'Medal';

  useEffect(() => {
    if (medal) {
      setStars(generateStars());
      setShowShare(false);
    }
  }, [medal]);

  const fetchGif = useCallback(async () => {
    if (!medal) return null;
    setGenerating(true);
    try {
      const res = await api.get(`/medals/${medal.medal_id}/gif`, { responseType: 'blob' });
      return res.data;
    } catch {
      toast.error('Failed to generate GIF');
      return null;
    } finally {
      setGenerating(false);
    }
  }, [medal]);

  const handleDownload = async () => {
    const blob = await fetchGif();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brioritize-medal-${medal.medal_type}.gif`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('GIF downloaded!');
  };

  const handleNativeShare = async () => {
    const blob = await fetchGif();
    if (!blob) return;
    const file = new File([blob], `brioritize-medal-${medal.medal_type}.gif`, { type: 'image/gif' });
    try {
      await navigator.share({ text: SHARE_TEXT, files: [file] });
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Sharing failed');
    }
  };

  const shareUrl = medal ? `${APP_URL}/shared/medal/${medal.medal_id}` : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(SHARE_TEXT)}`, '_blank');
  };
  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share && !!navigator.canShare;

  return (
    <AnimatePresence>
      {medal && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="medal-celebration"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Golden star burst */}
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
            {stars.map((star) => (
              <GoldenStar key={star.id} {...star} />
            ))}
          </div>

          {/* Medal card */}
          <motion.div
            className="relative z-10 max-w-sm w-full mx-4 bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
            initial={{ scale: 0.3, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 15, stiffness: 200, delay: 0.2 }}
          >
            {/* Gold header bar */}
            <div className="h-2 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-600" />

            <div className="p-6 text-center space-y-4">
              {/* Gold Medal */}
              <motion.div
                className="relative mx-auto"
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 12, stiffness: 120, delay: 0.5 }}
                style={{ width: 160, height: 160 }}
              >
                <GoldMedalSVG type={medal.medal_type} size={160} />
              </motion.div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1">
                  Medal Earned
                </p>
                <h2 className="text-xl font-bold font-['Manrope']" data-testid="medal-title">
                  {label}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Milestone: {medal.milestone} achievements
                </p>
              </motion.div>

              {/* Message */}
              <motion.p
                className="text-sm leading-relaxed text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0 }}
                data-testid="medal-message"
              >
                {medal.message}
              </motion.p>

              {/* Share / Continue */}
              <motion.div
                className="space-y-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
              >
                <AnimatePresence mode="wait">
                  {!showShare ? (
                    <motion.div key="actions" className="flex gap-2" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <Button
                        onClick={() => setShowShare(true)}
                        variant="outline"
                        className="flex-1 gap-2"
                        data-testid="medal-share-btn"
                      >
                        <Share2 className="w-4 h-4" />
                        Share
                      </Button>
                      <Button
                        onClick={onClose}
                        className="flex-1 bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 text-white border-0 hover:opacity-90"
                        data-testid="medal-close-btn"
                      >
                        Continue
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="share-panel"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      <p className="text-xs text-muted-foreground font-medium">Share your achievement</p>
                      <div className="flex justify-center gap-3">
                        <button onClick={shareToTwitter} className="w-10 h-10 rounded-full bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 flex items-center justify-center transition-colors" title="Share on X / Twitter" data-testid="share-twitter">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </button>
                        <button onClick={shareToFacebook} className="w-10 h-10 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors" title="Share on Facebook" data-testid="share-facebook">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </button>
                        <button onClick={shareToLinkedIn} className="w-10 h-10 rounded-full bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 flex items-center justify-center transition-colors" title="Share on LinkedIn" data-testid="share-linkedin">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </button>
                        {canNativeShare && (
                          <button onClick={handleNativeShare} className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors" title="Share via device" data-testid="share-native" disabled={generating}>
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      <Button onClick={handleCopyLink} variant="outline" className="w-full gap-2" data-testid="share-copy-link">
                        <Link2 className="w-4 h-4" />
                        Copy Link
                      </Button>
                      <Button onClick={handleDownload} variant="outline" className="w-full gap-2" disabled={generating} data-testid="share-download-gif">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Download GIF
                      </Button>
                      <Button onClick={onClose} className="w-full bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 text-white border-0 hover:opacity-90" data-testid="medal-close-btn">
                        Continue
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
